import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Download, ArrowLeft, FileText, Globe, Loader2, CheckCircle, Pencil, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAppContext } from "@/lib/onboardingContext";
import type { OnboardingData } from "@/lib/businessData";
import {
  getDefaultPlanOverrides,
  type PlanOverrides,
  type BrandOverrides,
} from "@/lib/pdfExport";
import { useAuth } from "@/lib/authContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatCityName } from "@/lib/utils";
import { getCityCenter, getNeighborhoodCoords } from "@/lib/neighborhoodCoordinates";

/**
 * Sidebar navigation entries. Each `id` matches an HTML id rendered by
 * the corresponding section in <PlanDocument /> so that clicks scroll
 * smoothly to that section and the IntersectionObserver scrollspy can
 * highlight the active one as the user scrolls.
 *
 * We keep two label sets (en / es) instead of a separate i18n file so
 * everything related to the sidebar lives in one place.
 */
type SectionEntry = { id: string; en: string; es: string };

const SECTION_ENTRIES: SectionEntry[] = [
  { id: "section-cover",       en: "Cover Page",         es: "Portada" },
  { id: "section-toc",         en: "Table of Contents",  es: "Índice" },
  { id: "section-exec",        en: "Executive Summary",  es: "Resumen Ejecutivo" },
  { id: "section-concept",     en: "Concept Overview",   es: "Visión del Concepto" },
  { id: "section-market",      en: "Market Analysis",    es: "Análisis de Mercado" },
  { id: "section-location",    en: "Location Analysis",  es: "Estrategia de Ubicación" },
  { id: "section-financials",  en: "Financial Projections", es: "Proyecciones Financieras" },
  { id: "section-startup",     en: "Cost Breakdown",     es: "Desglose de Costos" },
  { id: "section-staffing",    en: "Staffing Plan",      es: "Plan de Personal" },
  { id: "section-permits",     en: "Permits & Licenses", es: "Permisos y Licencias" },
  { id: "section-timeline",    en: "Timeline Roadmap",   es: "Cronograma" },
  { id: "section-risk",        en: "Risk Analysis",      es: "Análisis de Riesgos" },
  { id: "section-checklist",   en: "Checklist",          es: "Lista de Apertura" },
  { id: "section-mistakes",    en: "Common Mistakes",    es: "Errores Comunes" },
  { id: "section-first7",      en: "First 7 Days",       es: "Primeros 7 Días" },
];

type EditableField = {
  key: keyof PlanOverrides;
  label: string;
  multiline: boolean;
  rows?: number;
};

const EDITABLE_FIELDS: EditableField[] = [
  { key: "coverTitle", label: "Cover Title", multiline: false },
  { key: "coverSubtitle", label: "Cover Subtitle", multiline: false },
  { key: "executiveSummary", label: "Executive Summary", multiline: true, rows: 6 },
  { key: "conceptOverview", label: "Concept Overview", multiline: true, rows: 4 },
  { key: "marketAnalysis", label: "Market Analysis", multiline: true, rows: 4 },
  { key: "locationStrategy", label: "Location Strategy", multiline: true, rows: 4 },
  { key: "financialsCommentary", label: "Financial Projections — Commentary", multiline: true, rows: 5 },
  { key: "startupCostsCommentary", label: "Startup Costs — Commentary", multiline: true, rows: 3 },
  { key: "pnlCommentary", label: "Monthly P&L — Commentary", multiline: true, rows: 3 },
  { key: "staffingCommentary", label: "Staffing — Commentary", multiline: true, rows: 4 },
  { key: "permitsCommentary", label: "Permits — Commentary", multiline: true, rows: 3 },
  { key: "timelineCommentary", label: "Timeline — Commentary", multiline: true, rows: 3 },
  { key: "riskCommentary", label: "Risk Analysis — Commentary", multiline: true, rows: 4 },
];

export default function PlanPreview() {
  const navigate = useNavigate();
  const { results, onboarding } = useAppContext();
  const { user } = useAuth();
  const [lang, setLang] = useState<"en" | "es">("en");
  const [downloading, setDownloading] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [overrides, setOverrides] = useState<PlanOverrides>({});

  const dataReady = useMemo(() => {
    if (!results) return false;
    return !!(
      results.businessLabel &&
      results.city &&
      results.costBreakdown?.length > 0 &&
      results.timelineSteps?.length > 0 &&
      results.permits?.length > 0 &&
      results.staffing?.length > 0
    );
  }, [results]);

  // Storage key bound to business + city + language so different plans don't bleed
  const storageKey = useMemo(() => {
    if (!results) return null;
    return `oplif:plan-overrides:${onboarding.businessType ?? "_"}:${results.city}:${lang}`;
  }, [results, onboarding.businessType, lang]);

  // Load saved overrides whenever the storage key changes
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      setOverrides(raw ? (JSON.parse(raw) as PlanOverrides) : {});
    } catch {
      setOverrides({});
    }
  }, [storageKey]);

  // Persist on change (debounced via effect)
  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(overrides));
    } catch {
      /* ignore quota errors */
    }
  }, [storageKey, overrides]);

  const defaults = useMemo(
    () => (results ? getDefaultPlanOverrides(results, lang) : null),
    [results, lang],
  );

  // Default brand name pulled from the user's actual onboarding data so the
  // PDF is pre-personalised. Falls back to the business label only if nothing
  // was entered during onboarding.
  const defaultBrandName = useMemo(
    () => onboarding.businessName?.trim() || results?.businessLabel || "",
    [onboarding.businessName, results?.businessLabel],
  );

  // Merge the default into overrides for PDF generation, but only when the
  // user hasn't explicitly set a brand name themselves.
  const effectiveOverrides: PlanOverrides = useMemo(() => {
    if (!defaultBrandName) return overrides;
    if (overrides.brand?.brandName?.trim()) return overrides;
    return {
      ...overrides,
      brand: { ...overrides.brand, brandName: defaultBrandName },
    };
  }, [overrides, defaultBrandName]);

  // Pull defaults so we can render rich HTML for every section.
  // (effectiveOverrides above ensures the user's edits & brand info win.)

  if (!results || !dataReady) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <FileText className="w-12 h-12 text-muted-foreground" />
        <h2 className="text-xl font-medium text-foreground">No roadmap data found</h2>
        <p className="text-muted-foreground text-sm max-w-md text-center">
          Complete the onboarding flow first to generate your business plan.
        </p>
        <Button onClick={() => navigate("/onboarding")} className="gap-2">
          <ArrowLeft className="w-4 h-4" /> Go to Onboarding
        </Button>
      </div>
    );
  }

  const trackDownload = async (dlLang: "en" | "es") => {
    if (!user) return;
    try {
      await supabase.from("pdf_downloads").insert({
        user_id: user.id,
        language: dlLang,
        business_type: results.businessLabel,
        city: results.city,
      });
    } catch (e) {
      console.error("Failed to track download:", e);
    }
  };

  const handleDownload = async (dlLang: "en" | "es") => {
    if (!dataReady) {
      toast.error("Data not ready yet. Please wait.");
      return;
    }
    setDownloading(dlLang);
    try {
      // Switch the rendered preview to the requested language so the
      // printed output matches what the user is downloading.
      if (dlLang !== lang) setLang(dlLang);
      // Let React commit the new language before triggering print.
      await new Promise(r => setTimeout(r, 250));
      await trackDownload(dlLang);
      // Use the browser's print dialog → "Save as PDF" works in every modern
      // browser and avoids cross-browser PDF rendering issues.
      window.print();
      toast.success(
        dlLang === "en"
          ? "Use 'Save as PDF' in the print dialog."
          : "Usa 'Guardar como PDF' en el diálogo de impresión.",
      );
    } catch (err) {
      console.error("Print dialog error:", err);
      toast.error("Failed to open print dialog. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const updateOverride = (key: keyof PlanOverrides, value: string) => {
    setOverrides(prev => ({ ...prev, [key]: value }));
  };

  const resetField = (key: keyof PlanOverrides) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const resetAll = () => {
    setOverrides({});
    toast.success(lang === "es" ? "Ediciones restablecidas" : "Edits reset to defaults");
  };

  const updateBrand = (patch: Partial<BrandOverrides>) => {
    setOverrides(prev => {
      const nextBrand = { ...(prev.brand ?? {}), ...patch };
      // Strip empty/undefined keys so we can detect "no edits"
      (Object.keys(nextBrand) as Array<keyof BrandOverrides>).forEach(k => {
        const v = nextBrand[k];
        if (v === undefined || v === "" || v === null) delete nextBrand[k];
      });
      const next = { ...prev };
      if (Object.keys(nextBrand).length === 0) delete next.brand;
      else next.brand = nextBrand;
      return next;
    });
  };

  const handleImageUpload = async (
    file: File | null,
    field: "logoDataUrl" | "coverImageDataUrl",
    maxBytes = 2_500_000,
  ) => {
    if (!file) return;
    if (file.size > maxBytes) {
      toast.error(
        lang === "es"
          ? `La imagen es demasiado grande (máx ${(maxBytes / 1_000_000).toFixed(1)}MB)`
          : `Image is too large (max ${(maxBytes / 1_000_000).toFixed(1)}MB)`,
      );
      return;
    }
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
      toast.error(lang === "es" ? "Solo PNG o JPEG" : "PNG or JPEG only");
      return;
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    updateBrand({ [field]: dataUrl } as Partial<BrandOverrides>);
  };

  const brand = overrides.brand ?? {};
  const textEditedCount = (Object.entries(overrides) as Array<[string, unknown]>).filter(
    ([k, v]) => k !== "brand" && typeof v === "string" && v.trim().length > 0,
  ).length;
  const brandEditedCount = Object.values(brand).filter(v => v !== undefined && v !== "").length;
  const editedCount = textEditedCount + brandEditedCount;

  const r = results;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="gap-1 shrink-0">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
            <span className="font-heading text-base sm:text-lg font-medium text-foreground truncate">
              <FileText className="w-4 h-4 inline mr-1" />
              Business Plan
            </span>
            {dataReady && (
              <span className="flex items-center gap-1 text-xs text-emerald-600 shrink-0">
                <CheckCircle className="w-3 h-3" /> Ready
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <Button size="sm" variant={lang === "en" ? "default" : "outline"} onClick={() => setLang("en")} className="gap-1 h-8 text-xs">
              <Globe className="w-3 h-3" /> EN
            </Button>
            <Button size="sm" variant={lang === "es" ? "default" : "outline"} onClick={() => setLang("es")} className="gap-1 h-8 text-xs">
              <Globe className="w-3 h-3" /> ES
            </Button>
            <Button
              size="sm"
              variant={showEditor ? "default" : "outline"}
              onClick={() => setShowEditor(s => !s)}
              className="gap-1 h-8 text-xs"
            >
              <Pencil className="w-3 h-3" />
              {lang === "es" ? "Editar" : "Edit"}
              {editedCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-primary/15 text-primary px-1.5 text-[10px] font-medium">
                  {editedCount}
                </span>
              )}
            </Button>
            <Button size="sm" onClick={() => handleDownload("en")} disabled={!!downloading || !dataReady} className="gap-1 h-8 text-xs">
              {downloading === "en" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              <span className="hidden sm:inline">English</span> PDF
            </Button>
            <Button size="sm" onClick={() => handleDownload("es")} disabled={!!downloading || !dataReady} variant="outline" className="gap-1 h-8 text-xs">
              {downloading === "es" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              <span className="hidden sm:inline">Español</span> PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — sections list OR section editor */}
        {showEditor ? (
          <aside className="hidden lg:flex flex-col w-96 border-r border-border bg-card shrink-0">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-foreground">
                  {lang === "es" ? "Editar secciones" : "Edit sections"}
                </h4>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {lang === "es"
                    ? "Personaliza el texto antes de descargar"
                    : "Customize text before downloading"}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={resetAll}
                disabled={editedCount === 0}
                className="gap-1 h-7 text-[11px]"
                title={lang === "es" ? "Restablecer todas" : "Reset all"}
              >
                <RotateCcw className="w-3 h-3" />
                {lang === "es" ? "Restablecer" : "Reset all"}
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* ── Brand customization ── */}
              <div className="space-y-3 pb-4 border-b border-border">
                <div>
                  <h5 className="text-xs font-medium text-foreground uppercase tracking-wider">
                    {lang === "es" ? "Tu marca" : "Your brand"}
                  </h5>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {lang === "es"
                      ? "Logo, color y portada — guardados en este navegador"
                      : "Logo, color and cover — stored in this browser"}
                  </p>
                </div>

                {/* Brand name */}
                <div className="space-y-1.5">
                  <Label htmlFor="brand-name" className="text-xs font-medium text-foreground">
                    {lang === "es" ? "Nombre de marca" : "Brand name"}
                  </Label>
                  <Input
                    id="brand-name"
                    value={brand.brandName ?? ""}
                    onChange={e => updateBrand({ brandName: e.target.value })}
                    placeholder={defaultBrandName || "Your business name"}
                    className="text-xs h-8"
                  />
                </div>

                {/* Primary color */}
                <div className="space-y-1.5">
                  <Label htmlFor="brand-color" className="text-xs font-medium text-foreground">
                    {lang === "es" ? "Color principal" : "Primary color"}
                  </Label>
                  <div className="flex items-center gap-2">
                    <input
                      id="brand-color"
                      type="color"
                      value={brand.primaryHex ?? "#0d9488"}
                      onChange={e => updateBrand({ primaryHex: e.target.value })}
                      className="h-8 w-10 rounded border border-input bg-background cursor-pointer"
                      aria-label={lang === "es" ? "Selector de color" : "Color picker"}
                    />
                    <Input
                      value={brand.primaryHex ?? ""}
                      onChange={e => updateBrand({ primaryHex: e.target.value })}
                      placeholder="#0d9488"
                      className="text-xs h-8 flex-1 font-mono uppercase"
                      maxLength={7}
                    />
                    {brand.primaryHex && (
                      <button
                        type="button"
                        onClick={() => updateBrand({ primaryHex: undefined })}
                        className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {lang === "es" ? "Restablecer" : "Reset"}
                      </button>
                    )}
                  </div>
                </div>

                {/* Logo upload */}
                <div className="space-y-1.5">
                  <Label htmlFor="brand-logo" className="text-xs font-medium text-foreground">
                    {lang === "es" ? "Logo (PNG/JPEG)" : "Logo (PNG/JPEG)"}
                  </Label>
                  {brand.logoDataUrl ? (
                    <div className="flex items-center gap-2 p-2 rounded border border-input bg-muted/30">
                      <img
                        src={brand.logoDataUrl}
                        alt="Logo preview"
                        className="h-10 max-w-[120px] object-contain bg-background rounded"
                      />
                      <div className="flex flex-col gap-1 ml-auto">
                        <label className="text-[10px] text-primary hover:underline cursor-pointer">
                          {lang === "es" ? "Reemplazar" : "Replace"}
                          <input
                            type="file"
                            accept="image/png,image/jpeg"
                            className="hidden"
                            onChange={e => handleImageUpload(e.target.files?.[0] ?? null, "logoDataUrl")}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => updateBrand({ logoDataUrl: undefined })}
                          className="text-[10px] text-muted-foreground hover:text-destructive transition-colors text-left"
                        >
                          {lang === "es" ? "Quitar" : "Remove"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label
                      htmlFor="brand-logo"
                      className="flex flex-col items-center justify-center gap-1 p-3 rounded border border-dashed border-input bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors"
                    >
                      <span className="text-[11px] text-muted-foreground">
                        {lang === "es" ? "Subir logo (máx 2.5MB)" : "Upload logo (max 2.5MB)"}
                      </span>
                      <input
                        id="brand-logo"
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        onChange={e => handleImageUpload(e.target.files?.[0] ?? null, "logoDataUrl")}
                      />
                    </label>
                  )}
                </div>

                {/* Cover image upload */}
                <div className="space-y-1.5">
                  <Label htmlFor="brand-cover" className="text-xs font-medium text-foreground">
                    {lang === "es" ? "Imagen de portada" : "Cover image"}
                  </Label>
                  {brand.coverImageDataUrl ? (
                    <div className="space-y-2">
                      <img
                        src={brand.coverImageDataUrl}
                        alt="Cover preview"
                        className="w-full h-24 object-cover rounded border border-input"
                      />
                      <div className="flex items-center gap-3">
                        <label className="text-[10px] text-primary hover:underline cursor-pointer">
                          {lang === "es" ? "Reemplazar" : "Replace"}
                          <input
                            type="file"
                            accept="image/png,image/jpeg"
                            className="hidden"
                            onChange={e => handleImageUpload(e.target.files?.[0] ?? null, "coverImageDataUrl")}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => updateBrand({ coverImageDataUrl: undefined })}
                          className="text-[10px] text-muted-foreground hover:text-destructive transition-colors"
                        >
                          {lang === "es" ? "Quitar" : "Remove"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label
                      htmlFor="brand-cover"
                      className="flex flex-col items-center justify-center gap-1 p-3 rounded border border-dashed border-input bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors"
                    >
                      <span className="text-[11px] text-muted-foreground">
                        {lang === "es" ? "Subir portada (máx 2.5MB)" : "Upload cover (max 2.5MB)"}
                      </span>
                      <span className="text-[10px] text-muted-foreground/70">
                        {lang === "es" ? "Recomendado: 1600×900 o más" : "Recommended: 1600×900 or larger"}
                      </span>
                      <input
                        id="brand-cover"
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        onChange={e => handleImageUpload(e.target.files?.[0] ?? null, "coverImageDataUrl")}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* ── Section text editors ── */}
              {EDITABLE_FIELDS.map(field => {
                const defaultVal = defaults?.[field.key] ?? "";
                const currentVal = overrides[field.key];
                const isEdited = typeof currentVal === "string" && currentVal.trim().length > 0;
                const displayVal = isEdited ? (currentVal as string) : defaultVal;
                return (
                  <div key={field.key} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor={`field-${field.key}`}
                        className="text-xs font-medium text-foreground flex items-center gap-1.5"
                      >
                        {field.label}
                        {isEdited && (
                          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary" aria-label="edited" />
                        )}
                      </label>
                      {isEdited && (
                        <button
                          type="button"
                          onClick={() => resetField(field.key)}
                          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {lang === "es" ? "Restablecer" : "Reset"}
                        </button>
                      )}
                    </div>
                    {field.multiline ? (
                      <Textarea
                        id={`field-${field.key}`}
                        value={displayVal}
                        onChange={e => updateOverride(field.key, e.target.value)}
                        rows={field.rows ?? 4}
                        className="text-xs leading-relaxed resize-y"
                      />
                    ) : (
                      <Input
                        id={`field-${field.key}`}
                        value={displayVal}
                        onChange={e => updateOverride(field.key, e.target.value)}
                        className="text-xs h-8"
                      />
                    )}
                  </div>
                );
              })}
              <p className="text-[10px] text-muted-foreground pt-2 border-t border-border">
                {lang === "es"
                  ? "Los cambios se guardan automáticamente y se reflejan en la vista previa."
                  : "Edits auto-save and refresh the preview as you type."}
              </p>
            </div>
          </aside>
        ) : (
          <SectionNavSidebar lang={lang} />
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-muted/30">
          <PlanDocument
            results={r}
            lang={lang}
            overrides={effectiveOverrides}
            defaults={defaults}
            downloading={downloading}
            onDownload={handleDownload}
            onboarding={onboarding}
          />
        </main>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 * PlanDocument — renders the entire business plan as styled HTML
 * "pages". Each .plan-page is sized like an A4 sheet (210×297mm)
 * so the on-screen preview matches the printed/PDF output.
 * Print CSS in index.css forces real page breaks between them.
 * ──────────────────────────────────────────────────────────── */
type PlanDocResults = NonNullable<ReturnType<typeof useAppContext>["results"]>;

/* ────────────────────────────────────────────────────────────
 * SectionNavSidebar — the always-visible navigation sidebar.
 *
 *   • Each entry is a button that smoothly scrolls the matching
 *     <Page id="…"> into view.
 *   • An IntersectionObserver tracks which section is currently
 *     intersecting the top 30% of the viewport and highlights it.
 *   • Sections that aren't rendered (e.g. when the user has no
 *     checklist data) are skipped automatically.
 * ──────────────────────────────────────────────────────────── */
function SectionNavSidebar({ lang }: { lang: "en" | "es" }) {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Filter to entries that actually have a matching DOM element so we
  // don't show clicks-to-nowhere for conditionally rendered sections.
  const [availableIds, setAvailableIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Discover which sections are currently in the DOM. Re-run whenever
    // the language changes (because some sections disappear / reappear).
    const present = new Set<string>();
    for (const e of SECTION_ENTRIES) {
      if (document.getElementById(e.id)) present.add(e.id);
    }
    setAvailableIds(present);

    if (present.size === 0) return;

    // Highlight whichever section is closest to the top of the viewport.
    // rootMargin shifts the observer "viewport" so a section becomes
    // active a bit before it actually reaches the top of the screen.
    const observer = new IntersectionObserver(
      entries => {
        // Track all currently-intersecting section ids and pick the one
        // closest to the top.
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        // Start counting a section as "active" once its top crosses
        // ~25% from the viewport top; stop when its bottom passes ~70%.
        rootMargin: "-25% 0px -65% 0px",
        threshold: 0,
      },
    );

    for (const id of present) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [lang]);

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id); // optimistic — observer will confirm shortly
  };

  const visibleEntries = SECTION_ENTRIES.filter(e => availableIds.has(e.id));

  return (
    <aside className="hidden lg:block w-56 border-r border-border bg-card overflow-y-auto shrink-0">
      <div className="sticky top-0 bg-card z-[1] px-4 pt-4 pb-2 border-b border-border">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {lang === "es" ? "Secciones" : "Sections"}
        </h4>
      </div>
      <nav className="p-2">
        <ul className="space-y-0.5">
          {visibleEntries.map(entry => {
            const isActive = entry.id === activeId;
            return (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => handleClick(entry.id)}
                  aria-current={isActive ? "true" : undefined}
                  className={
                    "w-full text-left text-sm py-1.5 px-2 rounded transition-colors flex items-center gap-2 " +
                    (isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50")
                  }
                >
                  <span
                    aria-hidden
                    className={
                      "inline-block w-0.5 h-4 rounded-sm transition-colors " +
                      (isActive ? "bg-primary" : "bg-transparent")
                    }
                  />
                  <span className="truncate">{lang === "es" ? entry.es : entry.en}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}

function PlanDocument({
  results,
  lang,
  overrides,
  defaults,
  downloading,
  onDownload,
  onboarding,
}: {
  results: PlanDocResults;
  lang: "en" | "es";
  overrides: PlanOverrides;
  defaults: ReturnType<typeof getDefaultPlanOverrides> | null;
  downloading: string | null;
  onDownload: (l: "en" | "es") => void;
  onboarding: OnboardingData;
}) {
  const r = results;
  const brand = overrides.brand ?? {};
  const primary = brand.primaryHex || "#2d8653";
  const brandName = brand.brandName?.trim() || "oplif.";
  const sym = (r as { currencySymbol?: string }).currencySymbol || "$";
  const cityLabel = formatCityName(r.city);
  const today = new Date().toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
    year: "numeric", month: "long", day: "numeric",
  });

  // Resolve display text — user override wins, otherwise the default.
  const text = (key: keyof PlanOverrides): string => {
    const v = overrides[key];
    if (typeof v === "string" && v.trim()) return v;
    if (defaults && key in defaults) {
      // defaults excludes "brand"; key may be brand which we never call here.
      return (defaults as Record<string, string>)[key as string] ?? "";
    }
    return "";
  };

  const fmt$ = (n: number) => `${sym}${Math.round(n).toLocaleString()}`;

  const L = lang === "es"
    ? {
        plan: "Plan de Negocio", preparedFor: "Preparado para", date: "Fecha",
        toc: "Índice", page: "Página", confidential: "CONFIDENCIAL",
        execSummary: "Resumen Ejecutivo", concept: "Visión del Concepto",
        market: "Análisis de Mercado", location: "Estrategia de Ubicación",
        financials: "Proyecciones Financieras", startup: "Desglose de Costos Iniciales",
        pnl: "Pronóstico Mensual P&L", staffing: "Plan de Personal",
        permits: "Permisos y Cumplimiento", timeline: "Cronograma hasta la Apertura",
        risk: "Análisis de Riesgos", checklist: "Lista de Apertura",
        mistakes: "Errores Comunes", first7: "Primeros 7 Días",
        downloadEn: "Descargar PDF EN", downloadEs: "Descargar PDF ES",
        category: "Categoría", estimate: "Estimación", pctTotal: "% Total",
        total: "Total", role: "Rol", count: "Cantidad", rate: "Tarifa",
        monthly: "Costo Mensual", priority: "Prioridad",
        permit: "Permiso", required: "Requerido", processingTime: "Tiempo",
        cost: "Costo Est.", yes: "Sí", optional: "Opcional",
        step: "Fase", duration: "Duración", details: "Detalles",
        neighborhood: "Barrio", rentRange: "Rango de renta",
        traffic: "Tráfico", fit: "Puntuación",
        disclaimer: "Aviso: Las proyecciones son estimaciones. Verifique con autoridades locales y asesores cualificados antes de tomar decisiones financieras.",
      }
    : {
        plan: "Business Plan", preparedFor: "Prepared for", date: "Date",
        toc: "Table of Contents", page: "Page", confidential: "CONFIDENTIAL",
        execSummary: "Executive Summary", concept: "Concept Overview",
        market: "Market Analysis", location: "Location Strategy",
        financials: "Financial Projections", startup: "Startup Cost Breakdown",
        pnl: "Monthly P&L Forecast", staffing: "Staffing Plan",
        permits: "Permits & Licenses", timeline: "Timeline to Opening",
        risk: "Risk Analysis", checklist: "Opening Checklist",
        mistakes: "Common Mistakes to Avoid", first7: "First 7 Days",
        downloadEn: "Download English PDF", downloadEs: "Download Español PDF",
        category: "Category", estimate: "Estimate", pctTotal: "% of Total",
        total: "Total", role: "Role", count: "Count", rate: "Rate",
        monthly: "Monthly Cost", priority: "Priority",
        permit: "Permit", required: "Required", processingTime: "Processing",
        cost: "Est. Cost", yes: "Yes", optional: "Optional",
        step: "Step", duration: "Duration", details: "Details",
        neighborhood: "Neighborhood", rentRange: "Rent Range",
        traffic: "Traffic", fit: "Fit Score",
        disclaimer: "Disclaimer: Projections are model estimates. Independently verify with local authorities and qualified advisors before financial commitments.",
      };

  // List of TOC entries in render order
  const tocEntries = [
    L.execSummary, L.concept, L.market, L.location,
    L.financials, L.startup, L.staffing,
    L.permits, L.timeline, L.risk,
    ...(r.checklist?.length ? [L.checklist] : []),
    ...(r.commonMistakes?.length ? [L.mistakes] : []),
    ...(r.first7Days?.length ? [L.first7] : []),
  ];

  return (
    <div className="plan-print-root">
      {/* Sticky toolbar above the document — hidden in print */}
      <div className="plan-no-print sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border px-4 py-2 flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {lang === "es"
            ? "Vista previa en vivo — los cambios se reflejan al instante."
            : "Live preview — your edits appear instantly."}
        </p>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onDownload("en")} disabled={!!downloading} className="gap-1 h-8 text-xs">
            {downloading === "en" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            {L.downloadEn}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDownload("es")} disabled={!!downloading} className="gap-1 h-8 text-xs">
            {downloading === "es" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            {L.downloadEs}
          </Button>
        </div>
      </div>

      <div className="mx-auto max-w-[210mm] py-6 px-2 sm:px-6 space-y-6">
        {/* ───────── Cover ───────── */}
        <Page brandColor={primary} id="section-cover">
          <div className="flex flex-col h-full">
            {brand.coverImageDataUrl ? (
              <div
                className="w-full h-56 rounded-md bg-cover bg-center mb-8 plan-avoid-break"
                style={{ backgroundImage: `url(${brand.coverImageDataUrl})` }}
              />
            ) : (
              <div
                className="w-full h-56 rounded-md mb-8 flex items-center justify-center plan-avoid-break"
                style={{ background: `linear-gradient(135deg, ${primary}, ${shade(primary, -0.25)})` }}
              >
                {brand.logoDataUrl
                  ? <img src={brand.logoDataUrl} alt="" className="h-20 max-w-[60%] object-contain" />
                  : <span className="text-white text-3xl font-heading tracking-tight">{brandName}</span>}
              </div>
            )}
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-widest" style={{ color: primary }}>{L.plan}</p>
              <h1 className="text-4xl font-heading font-medium text-slate-900 leading-tight">
                {overrides.coverTitle?.trim() || `${r.businessLabel}`}
              </h1>
              <p className="text-lg text-slate-600">
                {overrides.coverSubtitle?.trim() || `${cityLabel} · ${r.timeline}`}
              </p>
            </div>
            <div className="mt-auto pt-10 border-t border-slate-200 grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{L.preparedFor}</p>
                <p className="text-slate-900 font-medium">{brandName}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{L.date}</p>
                <p className="text-slate-900 font-medium">{today}</p>
              </div>
            </div>
          </div>
        </Page>

        {/* ───────── TOC ───────── */}
        <Page brandColor={primary} header={brandName} headerRight={`${r.businessLabel} · ${cityLabel}`} id="section-toc">
          <h2 className="text-2xl font-heading font-medium mb-6 pb-2 border-b-2" style={{ borderColor: primary, color: "#0f172a" }}>
            {L.toc}
          </h2>
          <ol className="space-y-2 text-sm">
            {tocEntries.map((entry, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <span className="font-medium text-slate-900">{i + 1}.</span>
                <span className="text-slate-700 flex-1">{entry}</span>
                <span className="flex-1 border-b border-dotted border-slate-300 mx-1" />
                <span className="text-slate-500 text-xs">{i + 3}</span>
              </li>
            ))}
          </ol>
        </Page>

        {/* ───────── Executive Summary ───────── */}
        <SectionPage id="section-exec" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={`1. ${L.execSummary}`}>
          <Prose>{text("executiveSummary")}</Prose>
          <KeyMetrics
            items={[
              { label: L.preparedFor, value: brandName },
              { label: lang === "es" ? "Inversión Total" : "Total Investment", value: fmt$(r.totalEstimate) },
              { label: lang === "es" ? "Cronograma" : "Timeline", value: r.timeline },
              { label: lang === "es" ? "Puntuación" : "Opening Score", value: `${r.openingScore}/100` },
            ]}
            primary={primary}
          />
        </SectionPage>

        {/* ───────── Concept ───────── */}
        <SectionPage id="section-concept" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={`2. ${L.concept}`}>
          <Prose>{text("conceptOverview")}</Prose>
          <ConceptDetails
            primary={primary}
            lang={lang}
            businessLabel={r.businessLabel}
            cityLabel={cityLabel}
            onboarding={onboarding}
          />
        </SectionPage>

        {/* ───────── Market ───────── */}
        <SectionPage id="section-market" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={`3. ${L.market}`}>
          <Prose>{text("marketAnalysis")}</Prose>
          {r.neighborhoods && r.neighborhoods.length > 0 && (
            <ChartCard
              title={lang === "es" ? `Mapa de barrios recomendados — ${cityLabel}` : `Recommended Neighborhoods Map — ${cityLabel}`}
              primary={primary}
            >
              <NeighborhoodMap
                city={r.city}
                neighborhoods={r.neighborhoods.map(n => ({ name: n.name, fitScore: n.fitScore }))}
                primary={primary}
              />
            </ChartCard>
          )}
          {r.neighborhoods && r.neighborhoods.length > 0 && (
            <DocTable
              primary={primary}
              headers={[L.neighborhood, L.rentRange, L.traffic, L.fit]}
              rows={r.neighborhoods.map(n => [n.name, n.rentRange, n.trafficLevel, `${n.fitScore}/10`])}
            />
          )}
        </SectionPage>

        {/* ───────── Location ───────── */}
        <SectionPage id="section-location" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={`4. ${L.location}`}>
          <Prose>{text("locationStrategy")}</Prose>
          <ChartCard
            title={lang === "es" ? "Análisis competitivo" : "Competitive Analysis"}
            primary={primary}
          >
            <CompetitiveAnalysisTable
              businessLabel={r.businessLabel}
              city={cityLabel}
              primary={primary}
              lang={lang}
            />
          </ChartCard>
        </SectionPage>

        {/* ───────── Financials ───────── */}
        <SectionPage id="section-financials" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={`5. ${L.financials}`}>
          <Prose>{text("financialsCommentary")}</Prose>
          <ChartCard
            title={lang === "es" ? "Proyección de ingresos a 3 años" : "3-Year Revenue Projection"}
            primary={primary}
          >
            <RevenueProjectionChart
              monthlyLabor={r.totalMonthlyLabor || 12000}
              primary={primary}
              lang={lang}
              currencySymbol={sym}
            />
          </ChartCard>
          <ChartCard
            title={lang === "es" ? "Pronóstico de flujo de caja mensual — Año 1" : "Monthly Cash Flow Forecast — Year 1"}
            primary={primary}
          >
            <CashFlowChart
              monthlyLabor={r.totalMonthlyLabor || 12000}
              primary={primary}
              lang={lang}
              currencySymbol={sym}
            />
          </ChartCard>
          <Prose className="mt-3 text-xs">{text("pnlCommentary")}</Prose>
          <YearByYearTable
            monthlyLabor={r.totalMonthlyLabor || 12000}
            primary={primary}
            lang={lang}
            currencySymbol={sym}
          />
        </SectionPage>

        {/* ───────── Startup Costs ───────── */}
        <SectionPage id="section-startup" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={`6. ${L.startup}`}>
          <Prose>{text("startupCostsCommentary")}</Prose>
          <ChartCard
            title={lang === "es" ? "Distribución de costos iniciales" : "Startup Cost Distribution"}
            primary={primary}
          >
            <CostPieChart
              data={r.costBreakdown.map(c => ({ label: c.category, value: c.estimate }))}
              primary={primary}
              currencySymbol={sym}
            />
          </ChartCard>
          <DocTable
            primary={primary}
            headers={[L.category, L.estimate, L.pctTotal]}
            rows={[
              ...r.costBreakdown.map(c => [c.category, fmt$(c.estimate), `${c.percentage}%`]),
              [L.total, fmt$(r.totalEstimate), "100%"],
            ]}
            highlightLastRow
          />
          <ChartCard
            title={lang === "es" ? "Análisis de punto de equilibrio" : "Break-Even Analysis"}
            primary={primary}
          >
            <BreakEvenChart
              monthlyLabor={r.totalMonthlyLabor || 12000}
              totalEstimate={r.totalEstimate}
              primary={primary}
              lang={lang}
              currencySymbol={sym}
            />
          </ChartCard>
        </SectionPage>

        {/* ───────── Staffing ───────── */}
        <SectionPage id="section-staffing" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={`7. ${L.staffing}`}>
          <Prose>{text("staffingCommentary")}</Prose>
          <ChartCard
            title={lang === "es" ? "Costo laboral vs. ingresos (Año 1)" : "Labor Cost vs. Revenue (Year 1)"}
            primary={primary}
          >
            <LaborVsRevenueChart
              monthlyLabor={r.totalMonthlyLabor || 12000}
              primary={primary}
              lang={lang}
              currencySymbol={sym}
            />
          </ChartCard>
          <DocTable
            primary={primary}
            headers={[L.role, L.count, L.rate, L.monthly, L.priority]}
            rows={r.staffing.map(s => [s.role, `${s.count}`, s.hourlyRate, fmt$(s.monthlyEstimate), s.priority])}
          />
        </SectionPage>

        {/* ───────── Permits ───────── */}
        <SectionPage id="section-permits" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={`8. ${L.permits}`}>
          <Prose>{text("permitsCommentary")}</Prose>
          <DocTable
            primary={primary}
            headers={[L.permit, L.required, L.processingTime, L.cost]}
            rows={r.permits.map(p => [p.name, p.required ? L.yes : L.optional, p.processingTime, p.estimatedCost])}
          />
        </SectionPage>

        {/* ───────── Timeline ───────── */}
        <SectionPage id="section-timeline" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={`9. ${L.timeline}`}>
          <Prose>{text("timelineCommentary")}</Prose>
          <DocTable
            primary={primary}
            headers={[L.step, L.duration, L.details]}
            rows={r.timelineSteps.map(t => [t.step, t.duration, t.substeps.map(s => s.task).join(", ")])}
          />
        </SectionPage>

        {/* ───────── Risk ───────── */}
        <SectionPage id="section-risk" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={`10. ${L.risk}`}>
          <Prose>{text("riskCommentary")}</Prose>
          <RiskMatrix primary={primary} lang={lang} />
        </SectionPage>

        {/* ───────── Checklist ───────── */}
        {r.checklist && r.checklist.length > 0 && (
          <SectionPage id="section-checklist" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={L.checklist}>
            <ul className="space-y-2 text-sm text-slate-700">
              {r.checklist.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-0.5" style={{ color: primary }}>☐</span>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </SectionPage>
        )}

        {/* ───────── Common Mistakes ───────── */}
        {r.commonMistakes && r.commonMistakes.length > 0 && (
          <SectionPage id="section-mistakes" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={L.mistakes}>
            <ol className="space-y-3 text-sm text-slate-700 list-decimal list-inside">
              {r.commonMistakes.map((m, i) => <li key={i}>{m}</li>)}
            </ol>
          </SectionPage>
        )}

        {/* ───────── First 7 Days ───────── */}
        {r.first7Days && r.first7Days.length > 0 && (
          <SectionPage id="section-first7" primary={primary} brandName={brandName} subtitle={`${r.businessLabel} · ${cityLabel}`} title={L.first7}>
            <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
              {r.first7Days.map((d, i) => <li key={i}>{d}</li>)}
            </ol>
          </SectionPage>
        )}

        {/* ───────── Closing / disclaimer ───────── */}
        <Page brandColor={primary}>
          <div className="flex flex-col h-full text-slate-700">
            <h2 className="text-2xl font-heading font-medium mb-4" style={{ color: "#0f172a" }}>
              {lang === "es" ? "Notas finales" : "Closing Notes"}
            </h2>
            <p className="text-sm leading-relaxed">{L.disclaimer}</p>
            <div className="mt-auto pt-8 border-t border-slate-200 text-center text-[11px] text-slate-500">
              {L.confidential} · {brandName} · {today}
            </div>
          </div>
        </Page>
      </div>
    </div>
  );
}

/* A4-sized page (210×297mm). On-screen we cap width responsively and add a
 * subtle drop-shadow so each page feels like a real document sheet. */
function Page({
  children,
  brandColor,
  header,
  headerRight,
  id,
}: {
  children: React.ReactNode;
  brandColor: string;
  header?: string;
  headerRight?: string;
  id?: string;
}) {
  return (
    <article
      id={id}
      className="plan-page bg-white text-slate-900 shadow-elevated rounded-md overflow-hidden mx-auto"
      style={{
        width: "100%",
        maxWidth: "210mm",
        minHeight: "297mm",
        padding: "18mm",
        position: "relative",
        scrollMarginTop: "72px",
      }}
    >
      {/* Top brand bar */}
      <div
        aria-hidden
        style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 6,
          background: brandColor,
        }}
      />
      {(header || headerRight) && (
        <div className="flex items-center justify-between text-[10px] text-slate-500 mb-6 -mt-2">
          <span>{header}</span>
          <span>{headerRight}</span>
        </div>
      )}
      <div className="h-full">{children}</div>
    </article>
  );
}

function SectionPage({
  primary,
  brandName,
  subtitle,
  title,
  children,
  id,
}: {
  primary: string;
  brandName: string;
  subtitle: string;
  title: string;
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <Page brandColor={primary} header={brandName} headerRight={subtitle} id={id}>
      <h2
        className="text-2xl font-heading font-medium mb-6 pb-2 border-b-2"
        style={{ borderColor: primary, color: "#0f172a" }}
      >
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </Page>
  );
}

function Prose({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-sm leading-relaxed text-slate-700 whitespace-pre-wrap ${className}`}>
      {children}
    </p>
  );
}

function KeyMetrics({
  items,
  primary,
}: {
  items: { label: string; value: string }[];
  primary: string;
}) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 plan-avoid-break">
      {items.map((m, i) => (
        <div
          key={i}
          className="rounded-md border border-slate-200 bg-white p-3 relative overflow-hidden"
          style={{ borderTop: `3px solid ${primary}` }}
        >
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{m.label}</p>
          <p className="text-xl font-heading font-medium leading-tight mt-1" style={{ color: primary }}>{m.value}</p>
        </div>
      ))}
    </div>
  );
}

function DocTable({
  headers,
  rows,
  primary,
  highlightLastRow,
}: {
  headers: string[];
  rows: (string | number)[][];
  primary: string;
  highlightLastRow?: boolean;
}) {
  return (
    <div className="overflow-x-auto plan-avoid-break mt-3">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr style={{ background: primary }}>
            {headers.map((h, i) => (
              <th key={i} className="text-left py-2 px-3 text-white font-medium uppercase tracking-wider text-[10px]">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const isLast = highlightLastRow && i === rows.length - 1;
            return (
              <tr
                key={i}
                className={isLast ? "font-medium" : ""}
                style={{
                  background: isLast ? "#f1f5f9" : i % 2 === 0 ? "#ffffff" : "#f8fafc",
                }}
              >
                {row.map((cell, j) => (
                  <td key={j} className="py-2 px-3 text-slate-700 border-b border-slate-100 align-top">
                    {cell}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 * Chart primitives — all SVG, print-friendly, no extra deps.
 * Designed to feel "McKinsey-ish": muted grid, single brand-color
 * data series, generous whitespace, clear axis labels.
 * ──────────────────────────────────────────────────────────── */

function ChartCard({
  title,
  primary,
  children,
}: {
  title: string;
  primary: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 plan-avoid-break mt-3">
      <div className="flex items-center gap-2 mb-3">
        <span
          aria-hidden
          className="inline-block w-1 h-4 rounded-sm"
          style={{ background: primary }}
        />
        <h4 className="text-xs font-medium uppercase tracking-wider text-slate-700">
          {title}
        </h4>
      </div>
      {children}
    </div>
  );
}

function fmtMoney(n: number, sym: string): string {
  if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${sym}${Math.round(n / 1_000)}k`;
  return `${sym}${Math.round(n)}`;
}

/* Compute the same projection model used elsewhere so charts match the tables. */
function computeProjectionBase(monthlyLabor: number) {
  const targetLaborPct = 0.28;
  const baseMonthlyRevenue = Math.max(monthlyLabor / targetLaborPct, 25_000);
  return baseMonthlyRevenue;
}

function RevenueProjectionChart({
  monthlyLabor,
  primary,
  lang,
  currencySymbol,
}: {
  monthlyLabor: number;
  primary: string;
  lang: "en" | "es";
  currencySymbol: string;
}) {
  const base = computeProjectionBase(monthlyLabor);
  const ramp = [0.75, 1.0, 1.15];
  const years = ramp.map((r, i) => ({
    label: lang === "es" ? `Año ${i + 1}` : `Year ${i + 1}`,
    value: base * 12 * r,
  }));
  const max = Math.max(...years.map(y => y.value));
  const W = 520, H = 220, padL = 60, padR = 16, padT = 12, padB = 36;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const barW = chartW / years.length * 0.5;
  const gap = chartW / years.length;
  const yTicks = 4;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img">
      {/* Y-axis grid + labels */}
      {Array.from({ length: yTicks + 1 }).map((_, i) => {
        const v = (max / yTicks) * (yTicks - i);
        const y = padT + (chartH / yTicks) * i;
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#e2e8f0" strokeWidth={1} />
            <text x={padL - 8} y={y + 3} fontSize={10} textAnchor="end" fill="#64748b">
              {fmtMoney(v, currencySymbol)}
            </text>
          </g>
        );
      })}
      {/* Bars */}
      {years.map((y, i) => {
        const h = (y.value / max) * chartH;
        const x = padL + gap * i + (gap - barW) / 2;
        const yPos = padT + chartH - h;
        return (
          <g key={i}>
            <rect x={x} y={yPos} width={barW} height={h} fill={primary} rx={2} />
            <text x={x + barW / 2} y={yPos - 6} fontSize={11} textAnchor="middle" fill="#0f172a" fontWeight={500}>
              {fmtMoney(y.value, currencySymbol)}
            </text>
            <text x={x + barW / 2} y={H - 12} fontSize={11} textAnchor="middle" fill="#475569">
              {y.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function CashFlowChart({
  monthlyLabor,
  primary,
  lang,
  currencySymbol,
}: {
  monthlyLabor: number;
  primary: string;
  lang: "en" | "es";
  currencySymbol: string;
}) {
  const base = computeProjectionBase(monthlyLabor);
  const cogsPct = 0.30, rentPct = 0.08, opexPct = 0.10, marketingPct = 0.04;
  const months = lang === "es"
    ? ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"]
    : ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const data = months.map((_, i) => {
    const ramp = 0.5 + (0.5 * i) / 11;
    const rev = base * ramp;
    const cogs = rev * cogsPct;
    const labor = monthlyLabor * (i < 3 ? 0.85 : 1.0);
    const rent = base * rentPct;
    const opex = rev * opexPct;
    const marketing = rev * marketingPct;
    return rev - cogs - labor - rent - opex - marketing;
  });
  const max = Math.max(...data, 0);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const W = 520, H = 220, padL = 60, padR = 16, padT = 12, padB = 36;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const zeroY = padT + (max / range) * chartH;
  const barW = (chartW / data.length) * 0.7;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img">
      <line x1={padL} y1={padT} x2={padL} y2={padT + chartH} stroke="#cbd5e1" />
      <line x1={padL} y1={zeroY} x2={W - padR} y2={zeroY} stroke="#94a3b8" strokeDasharray="3 3" />
      <text x={padL - 8} y={padT + 8} fontSize={10} textAnchor="end" fill="#64748b">{fmtMoney(max, currencySymbol)}</text>
      <text x={padL - 8} y={padT + chartH} fontSize={10} textAnchor="end" fill="#64748b">{fmtMoney(min, currencySymbol)}</text>
      <text x={padL - 8} y={zeroY + 3} fontSize={10} textAnchor="end" fill="#64748b">0</text>
      {data.map((v, i) => {
        const x = padL + (chartW / data.length) * i + ((chartW / data.length) - barW) / 2;
        const h = Math.abs((v / range) * chartH);
        const y = v >= 0 ? zeroY - h : zeroY;
        const fill = v >= 0 ? primary : "#dc2626";
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={h} fill={fill} rx={1.5} opacity={0.9} />
            <text x={x + barW / 2} y={padT + chartH + 14} fontSize={9} textAnchor="middle" fill="#475569">
              {months[i]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function CostPieChart({
  data,
  primary,
  currencySymbol,
}: {
  data: { label: string; value: number }[];
  primary: string;
  currencySymbol: string;
}) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const W = 520, H = 240, cx = 120, cy = 120, r = 100;
  const palette = generatePalette(primary, data.length);
  let acc = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const angle = (d.value / total) * Math.PI * 2;
    const x1 = cx + r * Math.cos(acc);
    const y1 = cy + r * Math.sin(acc);
    const x2 = cx + r * Math.cos(acc + angle);
    const y2 = cy + r * Math.sin(acc + angle);
    const large = angle > Math.PI ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    const slice = { path, color: palette[i], pct: (d.value / total) * 100, label: d.label, value: d.value };
    acc += angle;
    return slice;
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img">
      {slices.map((s, i) => (
        <path key={i} d={s.path} fill={s.color} stroke="#fff" strokeWidth={1.5} />
      ))}
      {/* Legend */}
      {slices.map((s, i) => {
        const ly = 20 + i * 18;
        return (
          <g key={`l-${i}`}>
            <rect x={250} y={ly - 8} width={10} height={10} fill={s.color} rx={1} />
            <text x={266} y={ly} fontSize={11} fill="#0f172a">
              {s.label}
            </text>
            <text x={W - 16} y={ly} fontSize={11} fill="#64748b" textAnchor="end">
              {fmtMoney(s.value, currencySymbol)} · {s.pct.toFixed(0)}%
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function LaborVsRevenueChart({
  monthlyLabor,
  primary,
  lang,
  currencySymbol,
}: {
  monthlyLabor: number;
  primary: string;
  lang: "en" | "es";
  currencySymbol: string;
}) {
  const base = computeProjectionBase(monthlyLabor);
  const months = lang === "es"
    ? ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"]
    : ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const rev = months.map((_, i) => base * (0.5 + (0.5 * i) / 11));
  const labor = months.map((_, i) => monthlyLabor * (i < 3 ? 0.85 : 1.0));
  const max = Math.max(...rev);
  const W = 520, H = 240, padL = 60, padR = 16, padT = 12, padB = 50;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const x = (i: number) => padL + (chartW / (months.length - 1)) * i;
  const y = (v: number) => padT + chartH - (v / max) * chartH;
  const path = (vals: number[]) =>
    vals.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img">
      {[0, 1, 2, 3, 4].map(i => {
        const yy = padT + (chartH / 4) * i;
        const val = max * (1 - i / 4);
        return (
          <g key={i}>
            <line x1={padL} y1={yy} x2={W - padR} y2={yy} stroke="#e2e8f0" />
            <text x={padL - 8} y={yy + 3} fontSize={10} textAnchor="end" fill="#64748b">
              {fmtMoney(val, currencySymbol)}
            </text>
          </g>
        );
      })}
      {/* Revenue area */}
      <path d={`${path(rev)} L ${x(months.length - 1)} ${padT + chartH} L ${x(0)} ${padT + chartH} Z`} fill={primary} opacity={0.15} />
      <path d={path(rev)} stroke={primary} strokeWidth={2.5} fill="none" />
      {/* Labor line */}
      <path d={path(labor)} stroke="#dc2626" strokeWidth={2} fill="none" strokeDasharray="4 3" />
      {months.map((m, i) => (
        <text key={i} x={x(i)} y={padT + chartH + 14} fontSize={9} textAnchor="middle" fill="#475569">{m}</text>
      ))}
      {/* Legend */}
      <g>
        <rect x={padL} y={H - 18} width={10} height={3} fill={primary} />
        <text x={padL + 14} y={H - 13} fontSize={10} fill="#0f172a">
          {lang === "es" ? "Ingresos" : "Revenue"}
        </text>
        <rect x={padL + 90} y={H - 18} width={10} height={3} fill="#dc2626" />
        <text x={padL + 104} y={H - 13} fontSize={10} fill="#0f172a">
          {lang === "es" ? "Costo laboral" : "Labor cost"}
        </text>
      </g>
    </svg>
  );
}

function BreakEvenChart({
  monthlyLabor,
  totalEstimate,
  primary,
  lang,
  currencySymbol,
}: {
  monthlyLabor: number;
  totalEstimate: number;
  primary: string;
  lang: "en" | "es";
  currencySymbol: string;
}) {
  // Cumulative profit vs. cumulative startup investment recovery.
  const base = computeProjectionBase(monthlyLabor);
  const cogsPct = 0.30, rentPct = 0.08, opexPct = 0.10, marketingPct = 0.04;
  const N = 18; // 18-month horizon
  let cum = -totalEstimate;
  const series: number[] = [];
  for (let i = 0; i < N; i++) {
    const ramp = 0.5 + (0.5 * Math.min(i, 11)) / 11;
    const rev = base * ramp;
    const cogs = rev * cogsPct;
    const labor = monthlyLabor * (i < 3 ? 0.85 : 1.0);
    const rent = base * rentPct;
    const opex = rev * opexPct;
    const marketing = rev * marketingPct;
    cum += rev - cogs - labor - rent - opex - marketing;
    series.push(cum);
  }
  const breakEvenMonth = series.findIndex(v => v >= 0);
  const max = Math.max(...series);
  const min = Math.min(...series);
  const range = max - min || 1;
  const W = 520, H = 240, padL = 60, padR = 16, padT = 12, padB = 40;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const zeroY = padT + (max / range) * chartH;
  const x = (i: number) => padL + (chartW / (N - 1)) * i;
  const y = (v: number) => padT + chartH - ((v - min) / range) * chartH;
  const path = series.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img">
      {[0, 1, 2, 3, 4].map(i => {
        const yy = padT + (chartH / 4) * i;
        const val = max - (range / 4) * i;
        return (
          <g key={i}>
            <line x1={padL} y1={yy} x2={W - padR} y2={yy} stroke="#e2e8f0" />
            <text x={padL - 8} y={yy + 3} fontSize={10} textAnchor="end" fill="#64748b">
              {fmtMoney(val, currencySymbol)}
            </text>
          </g>
        );
      })}
      <line x1={padL} y1={zeroY} x2={W - padR} y2={zeroY} stroke="#94a3b8" strokeDasharray="3 3" />
      <path d={`${path} L ${x(N - 1)} ${zeroY} L ${x(0)} ${zeroY} Z`} fill={primary} opacity={0.12} />
      <path d={path} stroke={primary} strokeWidth={2.5} fill="none" />
      {breakEvenMonth >= 0 && (
        <g>
          <line
            x1={x(breakEvenMonth)}
            y1={padT}
            x2={x(breakEvenMonth)}
            y2={padT + chartH}
            stroke="#dc2626"
            strokeWidth={1.5}
            strokeDasharray="4 3"
          />
          <circle cx={x(breakEvenMonth)} cy={zeroY} r={4} fill="#dc2626" />
          <text x={x(breakEvenMonth) + 6} y={padT + 14} fontSize={11} fill="#dc2626" fontWeight={600}>
            {lang === "es" ? `Equilibrio: Mes ${breakEvenMonth + 1}` : `Break-even: Month ${breakEvenMonth + 1}`}
          </text>
        </g>
      )}
      {Array.from({ length: 6 }).map((_, k) => {
        const i = Math.round((N - 1) * (k / 5));
        return (
          <text key={k} x={x(i)} y={padT + chartH + 14} fontSize={9} textAnchor="middle" fill="#475569">
            {lang === "es" ? `M${i + 1}` : `M${i + 1}`}
          </text>
        );
      })}
    </svg>
  );
}

function NeighborhoodMap({
  city,
  neighborhoods,
  primary,
}: {
  city: string;
  neighborhoods: { name: string; fitScore: number }[];
  primary: string;
}) {
  const center = getCityCenter(city);
  const points = neighborhoods
    .map(n => ({ ...n, coords: getNeighborhoodCoords(city, n.name) }))
    .filter((n): n is typeof n & { coords: [number, number] } => !!n.coords);

  // If we have no coords, fall back to a clean ranked list visual.
  if (!center || points.length === 0) {
    return (
      <div className="space-y-2">
        {neighborhoods.slice(0, 6).map((n, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="text-xs text-slate-500 w-5">{i + 1}.</span>
            <span className="text-sm text-slate-800 flex-1">{n.name}</span>
            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${(n.fitScore / 10) * 100}%`, background: primary }}
              />
            </div>
            <span className="text-xs font-medium text-slate-700 w-10 text-right">{n.fitScore}/10</span>
          </div>
        ))}
      </div>
    );
  }

  const W = 520, H = 320;
  const lats = points.map(p => p.coords[0]);
  const lngs = points.map(p => p.coords[1]);
  const minLat = Math.min(...lats, center.lat) - 0.02;
  const maxLat = Math.max(...lats, center.lat) + 0.02;
  const minLng = Math.min(...lngs, center.lng) - 0.02;
  const maxLng = Math.max(...lngs, center.lng) + 0.02;
  const project = (lat: number, lng: number): [number, number] => [
    ((lng - minLng) / (maxLng - minLng)) * (W - 40) + 20,
    H - (((lat - minLat) / (maxLat - minLat)) * (H - 40) + 20),
  ];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img">
      {/* Stylised "map" backdrop */}
      <rect x={0} y={0} width={W} height={H} fill="#f8fafc" rx={4} />
      {/* Grid lines */}
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`v-${i}`} x1={(W / 8) * i} y1={0} x2={(W / 8) * i} y2={H} stroke="#e2e8f0" />
      ))}
      {Array.from({ length: 5 }).map((_, i) => (
        <line key={`h-${i}`} x1={0} y1={(H / 5) * i} x2={W} y2={(H / 5) * i} stroke="#e2e8f0" />
      ))}
      {/* City center marker */}
      {(() => {
        const [cx, cy] = project(center.lat, center.lng);
        return (
          <g>
            <circle cx={cx} cy={cy} r={5} fill="#94a3b8" />
            <text x={cx + 8} y={cy + 4} fontSize={10} fill="#475569">{formatCityName(city)}</text>
          </g>
        );
      })()}
      {/* Neighborhood markers — radius scales with fit score */}
      {points.map((p, i) => {
        const [cx, cy] = project(p.coords[0], p.coords[1]);
        const radius = 6 + p.fitScore;
        return (
          <g key={i}>
            <circle cx={cx} cy={cy} r={radius} fill={primary} opacity={0.25} />
            <circle cx={cx} cy={cy} r={5} fill={primary} stroke="#fff" strokeWidth={1.5} />
            <text x={cx + radius + 4} y={cy - 2} fontSize={11} fill="#0f172a" fontWeight={600}>
              {p.name}
            </text>
            <text x={cx + radius + 4} y={cy + 11} fontSize={10} fill="#475569">
              {p.fitScore}/10
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function CompetitiveAnalysisTable({
  businessLabel,
  city,
  primary,
  lang,
}: {
  businessLabel: string;
  city: string;
  primary: string;
  lang: "en" | "es";
}) {
  const headers = lang === "es"
    ? ["Competidor", "Posicionamiento", "Precio", "Fortalezas", "Debilidades"]
    : ["Competitor", "Positioning", "Price", "Strengths", "Weaknesses"];
  const rows: string[][] = lang === "es"
    ? [
        [`${businessLabel} (Nuestro)`, "Premium artesanal", "$$$", "Marca local, experiencia diferenciada", "Sin reconocimiento inicial"],
        [`Cadena nacional`, "Volumen masivo", "$$", "Reconocimiento, eficiencia operativa", "Experiencia genérica"],
        [`Operador local establecido`, "Comunidad fiel", "$$", "Base de clientes leal", "Innovación lenta"],
        [`Nuevo concepto independiente`, "Tendencia / nicho", "$$$", "Hype, redes sociales", "Operaciones inmaduras"],
      ]
    : [
        [`${businessLabel} (Ours)`, "Premium / artisan", "$$$", "Local brand, differentiated experience", "No initial brand awareness"],
        [`National chain`, "Mass volume", "$$", "Brand recognition, operational efficiency", "Generic experience"],
        [`Established local operator`, "Loyal community", "$$", "Loyal customer base", "Slow to innovate"],
        [`New independent concept`, "Trend / niche", "$$$", "Hype, social media buzz", "Immature operations"],
      ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr style={{ background: primary }}>
            {headers.map((h, i) => (
              <th key={i} className="text-left py-2 px-3 text-white font-medium uppercase tracking-wider text-[10px]">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i === 0 ? `${primary}10` : i % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
              {row.map((cell, j) => (
                <td
                  key={j}
                  className="py-2 px-3 text-slate-700 border-b border-slate-100 align-top"
                  style={i === 0 && j === 0 ? { fontWeight: 600, color: "#0f172a" } : undefined}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[10px] text-slate-500 mt-2 italic">
        {lang === "es"
          ? `Análisis indicativo del mercado de ${city}. Verifica con investigación local antes de decidir.`
          : `Indicative analysis for the ${city} market. Validate with local research before committing.`}
      </p>
    </div>
  );
}

/* Generate a small palette by tinting/shading the brand color so the pie
 * chart stays on-brand instead of using arbitrary categorical colors. */
function generatePalette(primary: string, n: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / Math.max(n - 1, 1); // 0 → 1
    // Spread between -0.35 (darker) and +0.55 (lighter)
    const amount = -0.35 + t * 0.9;
    out.push(shade(primary, amount));
  }
  return out;
}

/* Lightweight hex shader used for the cover gradient when no cover image
 * is provided. amount: -1 (black) … +1 (white). */
function shade(hex: string, amount: number): string {
  const m = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(m)) return hex;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  const adjust = (c: number) => {
    const t = amount < 0 ? 0 : 255;
    const p = Math.abs(amount);
    return Math.round((t - c) * p + c);
  };
  const toHex = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${toHex(adjust(r))}${toHex(adjust(g))}${toHex(adjust(b))}`;
}

/* ────────────────────────────────────────────────────────────
 * ConceptDetails — adds dense, structured concept context derived from
 * onboarding answers. Always renders four signal cards plus a
 * differentiators bullet list so the Concept page never feels sparse.
 * ──────────────────────────────────────────────────────────── */
function ConceptDetails({
  primary,
  lang,
  businessLabel,
  cityLabel,
  onboarding,
}: {
  primary: string;
  lang: "en" | "es";
  businessLabel: string;
  cityLabel: string;
  onboarding: OnboardingData;
}) {
  const T = lang === "es"
    ? {
        title: "Pilares del Concepto",
        type: "Tipo de Concepto",
        market: "Mercado Objetivo",
        service: "Estilo de Servicio",
        edge: "Ventaja Competitiva",
        differentiators: "Diferenciadores Clave",
        d1: "Producto y experiencia diseñados específicamente para el cliente local de " + cityLabel,
        d2: "Operaciones disciplinadas con procedimientos estandarizados desde el día uno",
        d3: "Programa de fidelización digital y estrategia de redes sociales hiperlocal",
        d4: "Modelo financiero conservador con punto de equilibrio claro",
        notSet: "Por definir",
      }
    : {
        title: "Concept Pillars",
        type: "Concept Type",
        market: "Target Market",
        service: "Service Style",
        edge: "Competitive Advantage",
        differentiators: "Key Differentiators",
        d1: `Product and experience purpose-built for the ${cityLabel} local customer`,
        d2: "Disciplined operations with standardized procedures from day one",
        d3: "Digital loyalty program and hyper-local social media strategy",
        d4: "Conservative financial model with a clearly defined break-even point",
        notSet: "To be defined",
      };

  const cap = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/[-_]/g, " ") : T.notSet;

  const cards = [
    { label: T.type, value: businessLabel },
    { label: T.market, value: cap(onboarding.targetCustomer) },
    { label: T.service, value: cap(onboarding.serviceStyle) || cap(onboarding.locationType) },
    { label: T.edge, value: cap(onboarding.goal) || (lang === "es" ? "Diferenciación local" : "Local differentiation") },
  ];

  return (
    <div className="mt-4 space-y-4 plan-avoid-break">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-700">{T.title}</h3>
      <div className="grid grid-cols-2 gap-3">
        {cards.map((c, i) => (
          <div
            key={i}
            className="rounded-md border border-slate-200 bg-slate-50 p-3"
            style={{ borderLeft: `3px solid ${primary}` }}
          >
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</p>
            <p className="text-sm font-medium text-slate-900 mt-0.5">{c.value || T.notSet}</p>
          </div>
        ))}
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-4">
        <h4 className="text-xs font-medium uppercase tracking-wider text-slate-700 mb-2">
          {T.differentiators}
        </h4>
        <ul className="space-y-1.5 text-sm text-slate-700">
          {[T.d1, T.d2, T.d3, T.d4].map((d, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: primary }} />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 * YearByYearTable — Year 1 / 2 / 3 monthly revenue breakdown shown as
 * a compact 3-row table. Uses the same projection model as the charts.
 * ──────────────────────────────────────────────────────────── */
function YearByYearTable({
  monthlyLabor,
  primary,
  lang,
  currencySymbol,
}: {
  monthlyLabor: number;
  primary: string;
  lang: "en" | "es";
  currencySymbol: string;
}) {
  const base = computeProjectionBase(monthlyLabor);
  const monthsShort = lang === "es"
    ? ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"]
    : ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const yearLabel = lang === "es" ? "Año" : "Year";
  const totalLabel = lang === "es" ? "Total Anual" : "Annual Total";

  // Year 1 ramps from 50% → 100%; Year 2 = 1.0x baseline; Year 3 = 1.15x
  const years = [
    { label: `${yearLabel} 1`, monthly: monthsShort.map((_, i) => base * (0.5 + (0.5 * i) / 11)) },
    { label: `${yearLabel} 2`, monthly: monthsShort.map(() => base * 1.0) },
    { label: `${yearLabel} 3`, monthly: monthsShort.map(() => base * 1.15) },
  ];

  return (
    <div className="mt-4 plan-avoid-break overflow-x-auto">
      <h4 className="text-xs font-medium uppercase tracking-wider text-slate-700 mb-2">
        {lang === "es" ? "Ingresos Mensuales — Año 1 / 2 / 3" : "Monthly Revenue — Year 1 / 2 / 3"}
      </h4>
      <table className="w-full text-[10px] border-collapse">
        <thead>
          <tr style={{ background: primary }}>
            <th className="text-left py-1.5 px-2 text-white font-medium uppercase tracking-wider text-[9px]">
              {yearLabel}
            </th>
            {monthsShort.map(m => (
              <th key={m} className="text-right py-1.5 px-1.5 text-white font-medium uppercase tracking-wider text-[9px]">
                {m}
              </th>
            ))}
            <th className="text-right py-1.5 px-2 text-white font-medium uppercase tracking-wider text-[9px]">
              {totalLabel}
            </th>
          </tr>
        </thead>
        <tbody>
          {years.map((y, i) => {
            const total = y.monthly.reduce((s, v) => s + v, 0);
            return (
              <tr key={i} style={{ background: i % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                <td className="py-1.5 px-2 font-medium text-slate-900 border-b border-slate-100">{y.label}</td>
                {y.monthly.map((v, j) => (
                  <td key={j} className="py-1.5 px-1.5 text-right text-slate-700 border-b border-slate-100">
                    {fmtMoney(v, currencySymbol)}
                  </td>
                ))}
                <td
                  className="py-1.5 px-2 text-right font-medium border-b border-slate-100"
                  style={{ color: primary }}
                >
                  {fmtMoney(total, currencySymbol)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[10px] text-slate-500 mt-2 italic">
        {lang === "es"
          ? "Modelo: Año 1 con curva de rampa (50% → 100%); Año 2 baseline; Año 3 con +15% de crecimiento orgánico."
          : "Model: Year 1 ramp curve (50% → 100%); Year 2 baseline; Year 3 with +15% organic growth."}
      </p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 * RiskMatrix — McKinsey-style risk register: category, likelihood,
 * impact, and mitigation. Likelihood / impact rendered as colored
 * pills so the table reads at a glance.
 * ──────────────────────────────────────────────────────────── */
function RiskMatrix({ primary, lang }: { primary: string; lang: "en" | "es" }) {
  type Sev = "Low" | "Medium" | "High";
  const sevColor = (s: Sev) =>
    s === "High" ? "#dc2626" : s === "Medium" ? "#d97706" : "#059669";
  const sevLabel = (s: Sev) => {
    if (lang === "es") return s === "High" ? "Alto" : s === "Medium" ? "Medio" : "Bajo";
    return s;
  };

  const headers = lang === "es"
    ? ["Riesgo", "Probabilidad", "Impacto", "Mitigación"]
    : ["Risk", "Likelihood", "Impact", "Mitigation"];

  const rows: { risk: string; likelihood: Sev; impact: Sev; mitigation: string }[] =
    lang === "es"
      ? [
          { risk: "Sobrecostos en construcción", likelihood: "High", impact: "High",
            mitigation: "Contrato a precio fijo, contingencia del 15%, revisiones semanales." },
          { risk: "Retraso en permisos", likelihood: "Medium", impact: "High",
            mitigation: "Pre-consulta con autoridades, expediente sin huecos, expediter dedicado." },
          { risk: "Rotación de personal clave", likelihood: "High", impact: "Medium",
            mitigation: "Salarios competitivos, bonos por permanencia, formación continua." },
          { risk: "Inflación de insumos", likelihood: "Medium", impact: "Medium",
            mitigation: "Múltiples proveedores, menú dinámico, revisión trimestral de precios." },
          { risk: "Ingresos por debajo de proyección", likelihood: "Medium", impact: "High",
            mitigation: "6 meses de runway, marketing local agresivo, programa de fidelización." },
          { risk: "Inspección sanitaria fallida", likelihood: "Low", impact: "High",
            mitigation: "Auditorías internas mensuales, ServSafe certificado, lista pre-inspección." },
          { risk: "Disputa con propietario", likelihood: "Low", impact: "High",
            mitigation: "Contrato revisado por abogado, opciones de renovación, cláusulas claras." },
        ]
      : [
          { risk: "Construction cost overruns", likelihood: "High", impact: "High",
            mitigation: "Fixed-price contract, 15% contingency, weekly cost reviews." },
          { risk: "Permit / licensing delays", likelihood: "Medium", impact: "High",
            mitigation: "Pre-consult with authorities, complete filing package, dedicated expediter." },
          { risk: "Key staff turnover", likelihood: "High", impact: "Medium",
            mitigation: "Competitive wages, retention bonuses, continuous training program." },
          { risk: "Input cost inflation", likelihood: "Medium", impact: "Medium",
            mitigation: "Multi-supplier strategy, dynamic menu engineering, quarterly price reviews." },
          { risk: "Revenue under projection", likelihood: "Medium", impact: "High",
            mitigation: "6-month runway reserve, aggressive local marketing, loyalty program." },
          { risk: "Failed health inspection", likelihood: "Low", impact: "High",
            mitigation: "Monthly internal audits, ServSafe certified team, pre-inspection checklist." },
          { risk: "Landlord dispute", likelihood: "Low", impact: "High",
            mitigation: "Attorney-reviewed lease, renewal options, clear assignment clauses." },
        ];

  const Pill = ({ s }: { s: Sev }) => (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium text-white"
      style={{ background: sevColor(s) }}
    >
      {sevLabel(s)}
    </span>
  );

  return (
    <div className="mt-4 plan-avoid-break overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr style={{ background: primary }}>
            {headers.map((h, i) => (
              <th key={i} className="text-left py-2 px-3 text-white font-medium uppercase tracking-wider text-[10px]">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
              <td className="py-2 px-3 font-medium text-slate-900 border-b border-slate-100 align-top">
                {row.risk}
              </td>
              <td className="py-2 px-3 border-b border-slate-100 align-top"><Pill s={row.likelihood} /></td>
              <td className="py-2 px-3 border-b border-slate-100 align-top"><Pill s={row.impact} /></td>
              <td className="py-2 px-3 text-slate-700 border-b border-slate-100 align-top">
                {row.mitigation}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[10px] text-slate-500 mt-2 italic">
        {lang === "es"
          ? "Matriz indicativa. Revise con su equipo y asesores antes de operacionalizar."
          : "Indicative risk register. Review with your team and advisors before operationalizing."}
      </p>
    </div>
  );
}
